import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import config from '@src/config';
import { useAuth } from '@src/context';
import paths from '@src/paths';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer (read-only dashboards)' },
  { value: 'editor', label: 'Editor (can update onboarding + assets)' },
  { value: 'admin', label: 'Admin (full access for this client)' },
];

const INITIAL_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  role: ROLE_OPTIONS[0].value,
};

function ClientMemberInvitePage() {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  useEffect(() => {
    if (!isStaff) return;
    setClientsLoading(true);
    defaultClient
      .request({ url: 'dashboard/clients/', method: 'GET' })
      .then((response) => {
        const list = Array.isArray(response?.data?.results)
          ? response.data.results
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setClients(list);
      })
      .catch(() => {
        setClients([]);
        toast.error('Could not load clients. Please retry.');
      })
      .finally(() => setClientsLoading(false));
  }, [isStaff]);

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [clients]);

  const loginUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    return `${origin}${config.LOGIN_URL}`;
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const handleCopyLogin = useCallback(() => {
    const link = loginUrl || config.LOGIN_URL;
    if (!navigator?.clipboard || !link) {
      toast.error('Clipboard is not available in this browser.');
      return;
    }
    navigator.clipboard.writeText(link).then(
      () => toast.success('Login link copied'),
      () => toast.error('Unable to copy link')
    );
  }, [loginUrl]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!isStaff) {
        toast.error('Only staff can invite new client members.');
        return;
      }
      if (!selectedClient?.id) {
        toast.error('Select a client to invite this user to.');
        return;
      }
      if (!form.email.trim()) {
        toast.error('Email is required.');
        return;
      }

      setSending(true);
      const payload = {
        client_id: selectedClient.id,
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        role: form.role,
      };

      defaultClient
        .request({
          url: 'accounts/auth/client-memberships/invite/',
          method: 'POST',
          payload,
        })
        .then((response) => {
          setLastInvite({
            membership: response?.data || null,
            email: payload.email,
            clientName: selectedClient.name,
          });
          toast.success('Invite sent. We emailed them a link to join.');
          setForm(INITIAL_FORM);
        })
        .catch(() => {
          toast.error('Unable to send invite. Please try again.');
        })
        .finally(() => setSending(false));
    },
    [form, isStaff, selectedClient]
  );

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isStaff) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This page is only available to staff users. Client members cannot invite other users.
        </Alert>
        <Button variant="contained" href={paths.dashboard.dailyPulse}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Invite client members</Typography>
        <Typography color="text.secondary">
          Create a user (or re-activate existing), add them to a client, and send them a password setup email that lands on the login screen.
        </Typography>
      </Stack>

      <Card component="form" onSubmit={handleSubmit}>
        <CardContent>
          <Stack spacing={3}>
            {clientsLoading && <LinearProgress />}
            <Stack spacing={1}>
              <Typography variant="subtitle2">Client</Typography>
              <Autocomplete
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                options={sortedClients}
                getOptionLabel={(option) => option?.name || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.name}
                    </Typography>
                    {option.status ? <Chip label={option.status} size="small" /> : null}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search clients"
                    placeholder="Start typing a client name"
                    required
                  />
                )}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={handleInputChange('firstName')}
                fullWidth
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={handleInputChange('lastName')}
                fullWidth
              />
            </Stack>

            <TextField
              label="Email"
              value={form.email}
              onChange={handleInputChange('email')}
              type="email"
              required
              fullWidth
            />

            <TextField
              label="Role"
              value={form.role}
              onChange={handleInputChange('role')}
              select
              helperText="Roles apply only to this client."
              fullWidth
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SendIcon />}
                disabled={sending}
              >
                {sending ? 'Sending invite…' : 'Send invite'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                We&apos;ll create or update the user, attach them to the client, and email them a password setup link.
              </Typography>
            </Stack>

            {lastInvite ? (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Alert
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={handleCopyLogin}
                    >
                      Copy login link
                    </Button>
                  }
                >
                  Sent an invite to <strong>{lastInvite.email}</strong> for{' '}
                  <strong>{lastInvite.clientName}</strong>. They can log in at {loginUrl || config.LOGIN_URL} after setting a password.
                </Alert>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

ClientMemberInvitePage.displayName = 'ClientMemberInvitePage';

export default ClientMemberInvitePage;
